import * as React from "react";
import { useNavigate } from "react-router";
import { Open20Regular } from "@fluentui/react-icons";
import { vorgangService, type VorgangPickerItem } from "../../../services/vorgangService";
import {
    Abschnittstitel,
    Chip,
    Fehlerzeile,
    Hinweis,
    Laedt,
    MehrLadenKnopf,
    Suchfeld,
    Zeile,
} from "./sidebarChrome";
import {
    baueStufen,
    verteileAufStufen,
    zeilenKey,
    type StufenKey,
    type VorgangZeile,
} from "./vorgaengeStufen";
import { INK, INK2 } from "../../chat/chatTheme";

/**
 * Reiter „Vorgänge" der rechten Mail-Leiste (J5).
 *
 * Gestufte Reihenfolge (Vorgänge des Kontakts → meine und Team-Vorgänge →
 * alle), durchgehende Suche und echtes Nachladen bis ans Ende der Menge.
 *
 * Datenquelle ist `GET /vorgang/picker` aus Cluster VORGANG: derselbe gestufte,
 * paginierte Endpoint, den auch der Zuweisungs-Picker benutzt. Er liefert die
 * Rangfolge serverseitig — drei getrennte Feed-Aufrufe nachzubauen hieße, die
 * Reihenfolge ein zweites Mal zu definieren (und sie später auseinanderlaufen
 * zu lassen).
 */

const SEITE = 25;

export type VorgaengeTabProps = {
    aktiv: boolean;
    contactId: number | null;
    contactName?: string | null;
    /** Nach der Navigation das Leisten-Overlay schließen. */
    onNavigated?: () => void;
};

export const VorgaengeTab: React.FC<VorgaengeTabProps> = ({
    aktiv,
    contactId,
    contactName,
    onNavigated,
}) => {
    const navigate = useNavigate();
    const [suche, setSuche] = React.useState("");
    const [suchtext, setSuchtext] = React.useState("");
    const [items, setItems] = React.useState<VorgangPickerItem[] | null>(null);
    const [gesamt, setGesamt] = React.useState<number | null>(null);
    const [laedt, setLaedt] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [versuch, setVersuch] = React.useState(0);
    const [aufgeklappt, setAufgeklappt] = React.useState<Set<StufenKey>>(() => new Set());

    // Jeder Tastendruck bricht den laufenden Aufruf ab; erst nach 300 ms Ruhe
    // wird gesucht.
    React.useEffect(() => {
        const t = window.setTimeout(() => setSuchtext(suche.trim()), 300);
        return () => window.clearTimeout(t);
    }, [suche]);

    // Neue Suche oder anderer Kontakt ⇒ von vorn.
    React.useEffect(() => {
        setItems(null);
        setGesamt(null);
        setFehler(null);
        setAufgeklappt(new Set());
    }, [contactId, suchtext]);

    const lade = React.useCallback(
        async (offset: number, signal?: AbortSignal) => {
            setLaedt(true);
            setFehler(null);
            try {
                const { items: neu, total } = await vorgangService.getPickerPage({
                    contactId,
                    q: suchtext || undefined,
                    limit: SEITE,
                    offset,
                    signal,
                });
                setGesamt(total);
                setItems((prev) => (offset === 0 ? neu : [...(prev ?? []), ...neu]));
            } catch (e: any) {
                // Ein Abbruch ist kein Fehler — er kommt vom nächsten Tastendruck.
                if (e?.name === "AbortError") return;
                setFehler("Vorgänge konnten nicht geladen werden.");
            } finally {
                setLaedt(false);
            }
        },
        [contactId, suchtext],
    );

    React.useEffect(() => {
        if (!aktiv || items !== null) return;
        const ac = new AbortController();
        void lade(0, ac.signal);
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, contactId, suchtext, versuch]);

    const geladen = items ?? [];
    const stufen = React.useMemo(
        () =>
            baueStufen({
                zeilen: verteileAufStufen(geladen),
                kontaktName: contactName ?? null,
                aufgeklappt,
            }),
        [geladen, contactName, aufgeklappt],
    );

    const restServer = gesamt != null ? Math.max(0, gesamt - geladen.length) : null;

    const oeffne = (z: VorgangZeile) => {
        // Eine To-do-Zeile führt auf ihren Eltern-Vorgang; ein standalone Todo
        // auf `/todos/:id`, das im Frontend ohnehin dorthin weiterleitet.
        const ziel =
            z.art === "todo"
                ? z.vorgangId != null
                    ? `/vorgaenge/${z.vorgangId}`
                    : `/todos/${z.id}`
                : `/vorgaenge/${z.id}`;
        navigate(ziel);
        onNavigated?.();
    };

    return (
        <>
            <Suchfeld
                wert={suche}
                onWert={setSuche}
                platzhalter="Vorgänge durchsuchen…"
                ariaLabel="Vorgänge durchsuchen"
            />

            {contactId == null && (
                <div style={{ fontSize: 12, color: INK2, marginBottom: 10, lineHeight: 1.4 }}>
                    Kein Kontakt zugewiesen — die Stufe „Vorgänge dieses Kontakts" entfällt.
                </div>
            )}

            {fehler && (
                <Fehlerzeile
                    text={fehler}
                    onRetry={() => {
                        setFehler(null);
                        setItems(null);
                        setVersuch((v) => v + 1);
                    }}
                />
            )}

            {!fehler && laedt && items === null && <Laedt />}

            {!fehler &&
                stufen.map((stufe) => (
                    <div key={stufe.key}>
                        <Abschnittstitel>{stufe.titel}</Abschnittstitel>
                        {stufe.zeilen.map((z) => (
                            <Zeile key={zeilenKey(z)} onClick={() => oeffne(z)}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        alignItems: "center",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            color: INK,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {z.titel || (z.art === "todo" ? "To-do" : "Vorgang")}
                                    </span>
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {z.art === "todo" && <Chip>To-do</Chip>}
                                        <Open20Regular style={{ color: INK2 }} />
                                    </span>
                                </div>
                                <div style={{ display: "flex", gap: 10, fontSize: 12, color: INK2 }}>
                                    {z.vorgangNbr && <span>{z.vorgangNbr}</span>}
                                    {z.status && <span>{z.status}</span>}
                                    {z.twv && <span>TWV: {z.twv}</span>}
                                    {z.frist && <span>Frist: {z.frist}</span>}
                                </div>
                                {z.kundeLabel && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: INK2,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {z.kundeLabel}
                                    </div>
                                )}
                            </Zeile>
                        ))}
                        {stufe.mehrLokal && (
                            <MehrLadenKnopf
                                text="weitere anzeigen"
                                onClick={() =>
                                    setAufgeklappt((prev) => new Set(prev).add(stufe.key))
                                }
                            />
                        )}
                    </div>
                ))}

            {!fehler && items !== null && stufen.length === 0 && (
                <Hinweis>
                    {suchtext ? "Kein Vorgang zu dieser Suche." : "Keine Vorgänge gefunden."}
                </Hinweis>
            )}

            {/* Echtes Nachladen: der Knopf nennt die Restmenge und holt sie
                seitenweise, bis die Gesamtzahl erreicht ist. */}
            {!fehler && restServer != null && restServer > 0 && (
                <MehrLadenKnopf
                    text={laedt ? "lädt …" : `noch ${restServer} weitere laden`}
                    disabled={laedt}
                    onClick={() => void lade(geladen.length)}
                />
            )}
        </>
    );
};
